import { PieChart, PieChartOption, ChartHandle, Widget, Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowResizeListener } from '../../../../hooks/common';
import useRefEventListener from '../../../../hooks/common/useRefEventListener';
import useFetchCreditRiskLevel from '../../../../hooks/riskProducts/dashboard/useFetchCreditRiskLevel';
import useFetchWidgetMetrics from '../../../../hooks/riskProducts/dashboard/useFetchWidgetMetrics';
import { CreditRiskLevelMetric } from '../../../../services/dtos/riskProducts/creditRiskLevels.dto';
import { WIDGET_ID } from '../../../../services/dtos/riskProducts/widgetMetric.dto';
import { downloadPng, getChartSubHeader } from '../../../../utils/chartUtils';
import { sleep } from '../../../../utils/helperUtils';
import { useGlobalModalContext, MODAL_TYPES } from '../../../GlobalModal';
import NoResults from '../../../NoResults';
import ChartActions from '../ChartActions';
import { getChartConfig, WIDGET_TITLE } from './CreditRiskLevel.logic';
import styles from './CreditRiskLevel.module.scss';

const cx = classNames.bind(styles);

interface RiskLevelProps {
  watchListId: string;
}

const CreditRiskLevel = ({ watchListId }: RiskLevelProps): ReactElement => {
  const { showModal } = useGlobalModalContext();
  const chartHandle = useRef<ChartHandle>(null);
  const [selectedCreditRiskMetric, setSelectedCreditRiskMetric] = useState<CreditRiskLevelMetric | undefined>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [unratedToggleOn, setUnratedToggleOn] = useState(false);
  const { data, isLoading, isError, refetch } = useFetchCreditRiskLevel(
    watchListId,
    selectedCreditRiskMetric,
    !unratedToggleOn
  );
  const { data: widgetMetrics } = useFetchWidgetMetrics();
  const widgetId = WIDGET_ID.RISK_LEVEL;

  const onDonutClickRef = useRefEventListener((event) => {
    if (data) {
      showModal(MODAL_TYPES.CHART_DRILL_DOWN, {
        title: WIDGET_TITLE,
        subHeader: getChartSubHeader(widgetMetrics, selectedCreditRiskMetric, event.name, event.value, widgetId),
        companies: data[event.dataIndex].companyIds,
      });
    }
  });

  const onChartInitialized = () => {
    chartHandle.current?.chartInstance?.on('click', (event) => onDonutClickRef.current(event));
  };

  useEffect(() => {
    return () => {
      chartHandle.current?.chartInstance?.dispose();
    };
  }, []);

  useWindowResizeListener(() => chartHandle.current?.chartInstance?.resize());

  const chartOptions: PieChartOption | undefined = useMemo(() => {
    return getChartConfig(data, selectedCreditRiskMetric);
  }, [selectedCreditRiskMetric, data]);

  const onDownload = useCallback(async () => {
    if (chartHandle.current && chartHandle.current.chartInstance) {
      setIsDownloading(true);

      chartHandle.current.chartInstance.setOption(
        getChartConfig(data, selectedCreditRiskMetric, widgetMetrics?.RISK_LEVEL, true)!,
        true
      );
      downloadPng(chartHandle.current.chartInstance, `risk-summary-${selectedCreditRiskMetric}`);
      chartHandle.current.chartInstance.setOption(chartOptions!, true);

      await sleep(500);

      setIsDownloading(false);
    }
  }, [selectedCreditRiskMetric, chartOptions, data]);

  const renderFirstLine = () => {
    if (isError) {
      return undefined;
    }

    return (
      <ChartActions
        widgetId={widgetId}
        isDownloadDisabled={isLoading || isEmpty(data) || isDownloading}
        onDownload={onDownload}
        onMetricSelect={(value) => setSelectedCreditRiskMetric(value as CreditRiskLevelMetric)}
        onUnratedToggle={setUnratedToggleOn}
        selectedMetric={selectedCreditRiskMetric}
        unratedToggleOn={unratedToggleOn}
        showUnratedToggleTitle={false}
        dataTestId="credit-risk-level"
      />
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return undefined;
    }
    if (isEmpty(data)) {
      return <NoResults />;
    }

    return (
      <div className={cx('chart-container')}>
        <PieChart ref={chartHandle} option={chartOptions} onChartInitialized={onChartInitialized} />
        {isDownloading && (
          <div className={cx('download-loader-container')}>
            <Loader position="center" />
          </div>
        )}
      </div>
    );
  };

  return (
    <Widget
      classNames={cx('body')}
      headerTitle={WIDGET_TITLE}
      firstLine={renderFirstLine()}
      hideFirstLineWhenLoading={false}
      isLoading={isLoading}
      isError={isError}
      onRetryAfterErrorClick={refetch}
      dataTestId="watchlist-risk-level-widget"
    >
      {renderContent()}
    </Widget>
  );
};

export default CreditRiskLevel;
